import { contentText as sharedContentText, DEFAULT_PRICING_CONFIG } from '@menage/shared';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export type ContentLang = 'fr' | 'en';

/**
 * Pricing-engine line labels stored on quotes in the language of the request
 * (see estimatePrice in @menage/shared): shown in the interface language.
 */
const PRICING_LABELS: [string, string][] = [
  ['Prix de base', 'Base price'],
  ['Chambres', 'Bedrooms'],
  ['Salles de bain', 'Bathrooms'],
  ['Cuisines', 'Kitchens'],
  ['Espaces familiaux', 'Family rooms'],
  ['Étages supplémentaires', 'Extra floors'],
  ['Superficie additionnelle (pi²)', 'Additional area (sq ft)'],
  ['Animaux', 'Pets'],
  ['Ajustement type de service', 'Service type adjustment'],
  ...DEFAULT_PRICING_CONFIG.extras.map((e): [string, string] => [e.labelFr, e.labelEn]),
];
const LABEL_TO_EN = new Map(PRICING_LABELS);
const LABEL_TO_FR = new Map(PRICING_LABELS.map(([fr, en]): [string, string] => [en, fr]));

/** Known demo or system text in the requested language; anything else unchanged. */
export function contentText(text: string | null | undefined, lang: ContentLang): string {
  if (text == null) return '';
  return (lang === 'en' ? LABEL_TO_EN : LABEL_TO_FR).get(text) ?? sharedContentText(text, lang);
}

export const asContentLang = (lang: string | undefined): ContentLang => (lang === 'fr' ? 'fr' : 'en');

/**
 * Display helper for stored free text (property notes, job instructions, chat
 * messages...). Known demo content is shown in the interface language; real user
 * content is returned unchanged. Never use it on values being edited in a form.
 */
export function useContentText() {
  const { i18n } = useTranslation();
  const lang = asContentLang(i18n.language);
  return useCallback((text?: string | null) => contentText(text, lang), [lang]);
}

/** A checklist task label in the interface language (English label when one exists). */
export function taskLabel(task: { label: string; labelEn?: string | null }, lang: string | undefined): string {
  if (asContentLang(lang) === 'en') return task.labelEn || contentText(task.label, 'en');
  return contentText(task.label, 'fr');
}

/** Hook form of taskLabel bound to the interface language. */
export function useTaskLabel() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  return useCallback((task: { label: string; labelEn?: string | null }) => taskLabel(task, lang), [lang]);
}
